import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Checkbox, DialogContentText, FormControlLabel, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { BaseDialog } from './base_dialog';
import { type DialogKey, useDialogStore } from './dialogStore';

const DIALOG_KEY: DialogKey = 'confirmationDialog';

export function ConfirmationDialog() {
  const { t } = useLingui();
  const close = useDialogStore((s) => s.closeDialog);
  const data = useDialogStore((s) => s.getDialogData('confirmationDialog'));
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (data) setDontShowAgain(false);
  }, [data]);

  if (!data) return null;

  const {
    title,
    content,
    confirmLabel = t`Confirm`,
    cancelLabel = t`Cancel`,
    onConfirm,
    onCancel,
    isDestructive,
    showDontShowAgain,
    onDontShowAgain,
  } = data;

  const handleClose = () => {
    onCancel?.();
    close(DIALOG_KEY);
  };

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (showDontShowAgain && dontShowAgain) onDontShowAgain?.(true);
    onConfirm();
    close(DIALOG_KEY);
  };

  return (
    <BaseDialog
      dialogKey={DIALOG_KEY}
      title={title}
      onClose={handleClose}
      onSubmit={handleConfirm}
      maxWidth="xs"
      actions={
        <>
          <Button onClick={handleClose} variant="outlined" size="large" fullWidth>
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            color={isDestructive ? 'error' : 'primary'}
            variant="contained"
            size="large"
            fullWidth
            autoFocus
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <DialogContentText>{content}</DialogContentText>
      {showDontShowAgain && (
        <FormControlLabel
          control={<Checkbox checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />}
          label={
            <Typography variant="body1" color="text.secondary">
              <Trans>Don't show again</Trans>
            </Typography>
          }
          sx={{ mt: 2 }}
        />
      )}
    </BaseDialog>
  );
}
